/**
 * Gibbs user preferences — server-backed, memory-cached.
 *
 * Stores preferences via the `/services/prefs/` endpoint with a `gibbs.`
 * key prefix. Loaded once on init (non-blocking, falls back to defaults),
 * cached in memory for synchronous reads, saved immediately on change.
 *
 * @module gibbs/preferences
 */

/** Timeout for the initial preferences load (ms). */
const LOAD_TIMEOUT_MS = 2000;

/** Prefix for all Gibbs preference keys in the prefs service. */
const KEY_PREFIX = 'gibbs.';

/** Endpoint for loading preferences. */
const GET_PREFS_URL = '/services/prefs/getUserPrefs';

/** Endpoint for saving a single preference. */
const SAVE_PREF_URL = '/services/prefs/changeUserPref';

/**
 * Supported preference keys and their default values.
 * @type {Object<string, *>}
 */
const PREFERENCE_DEFAULTS = {
    defaultTier: 'command',
    preferredScoringFunctions: [],
    defaultExportFormat: 'markdown',
    favoriteTemplates: [],
    recentActions: [],
    autoExecuteSingleActions: false,
    autoSaveWorkflows: false,
    verbosity: 'normal',
    // User-defined medchem rules — extends the built-in ISOSTERE_RULES,
    // TOXICOPHORE_RULES, HETEROAROMATIC_SWAPS at lookup time. See
    // gibbs/knowledge/medchemRuleLookup.js.
    userBioisosteres: [],
    userMitigations: [],
    userRingSwaps: [],
    // Workspace-scoped ranker weights map. Key = workspace identifier
    // (protein PDB id or 'default'). Value = { primaryScorePreference,
    // tiebreakByFlagCount } overriding the defaultWeights from
    // gibbs/results/compoundRanker.js. See gibbs-ranker-weights-ui study.
    rankerWeights: {},
    // Active decision-chooser frame id (gibbs-question-chooser). Empty
    // string = no frame active. See gibbs/question-chooser/frames.js
    // for the v1 catalog.
    activeFrame: '',
};

/** In-memory preference cache. Initialized with defaults. */
let prefCache = { ...PREFERENCE_DEFAULTS };

/** Whether preferences have been loaded from the server. */
let loaded = false;

/** Promise that resolves when initial load completes (or times out). */
let loadPromise = null;

/**
 * Load all Gibbs preferences from the server.
 *
 * Non-blocking: if the prefs API fails or times out (2s), the cache
 * retains default values and the promise still resolves.
 *
 * @returns {Promise<Object>} The preferences cache after loading.
 */
export function loadPreferences() {
    if (loadPromise) return loadPromise;

    loadPromise = fetchPreferences().then((serverPrefs) => {
        // Merge server values over defaults. Values from the server are
        // stored as JSON strings for array/object types, so parse them.
        for (const [key, value] of Object.entries(serverPrefs)) {
            const defaultVal = PREFERENCE_DEFAULTS[key];
            // Arrays and plain objects are stored as JSON strings server-
            // side (see savePreference). Parse them back on load. Scalar
            // values (strings, booleans, numbers) pass through unchanged.
            const isComplexType = Array.isArray(defaultVal)
                || (typeof defaultVal === 'object' && defaultVal !== null);
            if (isComplexType) {
                try {
                    prefCache[key] = JSON.parse(value);
                } catch (_) {
                    prefCache[key] = defaultVal;
                }
            } else {
                prefCache[key] = value;
            }
        }
        loaded = true;
        return prefCache;
    }).catch(() => {
        // Silently fall back to defaults on any failure.
        loaded = true;
        return prefCache;
    });

    return loadPromise;
}

/**
 * Fetch preferences from the server with a timeout.
 *
 * @returns {Promise<Object>} Map of preference keys (without prefix) to values.
 * @private
 */
async function fetchPreferences() {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), LOAD_TIMEOUT_MS);

    try {
        const response = await fetch(GET_PREFS_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ keyPrefix: KEY_PREFIX }),
            signal: controller.signal,
        });
        clearTimeout(timer);

        if (!response.ok) return {};
        return await response.json();
    } catch (_) {
        clearTimeout(timer);
        return {};
    }
}

/**
 * Save a preference to the server and update the in-memory cache.
 *
 * The cache is updated immediately so the UI is responsive. The server
 * call is best-effort but the result is returned so callers can
 * surface persistence failures (e.g., the prefs endpoint isn't
 * deployed on the dev server — known limitation).
 *
 * @param {string} key - Preference key (without `gibbs.` prefix).
 * @param {*} value - Preference value.
 * @returns {Promise<{ success: boolean, error: string|null }>}
 */
export async function savePreference(key, value) {
    if (!(key in PREFERENCE_DEFAULTS)) {
        console.warn(`[gibbs/preferences] Unknown preference key: ${key}`);
    }

    prefCache[key] = value;

    // Serialize arrays/objects to JSON strings for the key-value store.
    const serialized = (typeof value === 'object' && value !== null)
        ? JSON.stringify(value)
        : value;

    let response;
    try {
        response = await fetch(SAVE_PREF_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ key: `${KEY_PREFIX}${key}`, value: serialized }),
        });
    } catch (err) {
        const msg = err && err.message ? err.message : 'network';
        console.warn(`[gibbs/preferences] Failed to save preference "${key}":`, msg);
        return { success: false, error: `network: ${msg}` };
    }

    if (!response.ok) {
        const code = `http_${response.status}`;
        console.warn(`[gibbs/preferences] Save returned ${response.status} for "${key}"`);
        return { success: false, error: code };
    }

    return { success: true, error: null };
}

/**
 * Get a preference value from the in-memory cache.
 *
 * @param {string} key - Preference key (without `gibbs.` prefix).
 * @param {*} [defaultValue] - Fallback if the key is not in the cache.
 *     If omitted, the built-in default for that key is used.
 * @returns {*} The preference value.
 */
export function getPreference(key, defaultValue) {
    if (key in prefCache) return prefCache[key];
    if (defaultValue !== undefined) return defaultValue;
    if (key in PREFERENCE_DEFAULTS) return PREFERENCE_DEFAULTS[key];
    return undefined;
}

/**
 * Serialize the current preferences into a concise context string
 * suitable for injection into LLM prompts (~200 tokens).
 *
 * @returns {string}
 */
export function serializePreferencesContext() {
    const lines = ['User Preferences:'];

    const tier = getPreference('defaultTier');
    lines.push(`- Preferred tier: ${tier}`);

    const scoring = getPreference('preferredScoringFunctions');
    if (scoring.length > 0) {
        lines.push(`- Scoring: ${scoring.join(', ')}`);
    }

    const exportFmt = getPreference('defaultExportFormat');
    lines.push(`- Export format: ${exportFmt}`);

    const templates = getPreference('favoriteTemplates');
    if (templates.length > 0) {
        lines.push(`- Favorite templates: ${templates.join(', ')}`);
    }

    const recent = getPreference('recentActions');
    if (recent.length > 0) {
        lines.push(`- Recent actions: ${recent.join(', ')}`);
    }

    return lines.join('\n');
}

/**
 * Get all current preferences as a plain object (copy).
 *
 * @returns {Object<string, *>}
 */
export function getAllPreferences() {
    return { ...prefCache };
}

/**
 * Whether preferences have been loaded (or load attempt completed).
 *
 * @returns {boolean}
 */
export function isLoaded() {
    return loaded;
}

/**
 * Reset the module state. Intended for testing only.
 * @private
 */
export function resetForTesting() {
    prefCache = { ...PREFERENCE_DEFAULTS };
    loaded = false;
    loadPromise = null;
}
